import { Alert, Box, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useContext, useState } from 'react';
import { ActivityFormContext, ActivityForms } from '@/contexts/ActivityFormContext';
import { validateName } from '@/components/utils/Validate';
import { useTranslation } from 'react-i18next';
import { ActivityContext } from 'src/contexts/ActivityContext';

const indicator = {
  borderRadius: '50%',
  width: '2em',
  textAlign: 'center',
  fontSize: '1.25rem',
  aspectRatio: '1',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: 'text.primary',
  boxShadow: 'none',
  bgcolor: 'secondary.main',
  zIndex: 1,
  cursor: 'pointer',
};

const connector = {
  bgcolor: 'secondary.main',
  width: '2em',
  height: '0.5em',
};

// Mellanrummet i ringen har sidans färg (var vitt, en ljus ring i mörkt läge).
const active = (primaryColor: string, gapColor: string) => ({
  boxShadow: `0 0 0 2px ${gapColor}, 0 0 0 4px ${primaryColor}`,
});

const completedBg = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
};

const ProgressIndicator = () => {
  const formHandler = useContext(ActivityFormContext);
  const { activity } = useContext(ActivityContext);
  const progress = formHandler.current.progressValue ?? 0;
  const totalSteps = formHandler.current.progressTotal ?? 3;
  const theme = useTheme();
  const { t } = useTranslation();
  const [navigationError, setNavigationError] = useState<string>('');

  const getBgSx = (identifier: number) => (progress >= identifier ? completedBg : {});
  const getActiveSx = (identifier: number) =>
    progress === identifier
      ? active(theme.palette.primary.main, theme.palette.background.default)
      : {};

  const getIndicatorSx = (identifier: number) => {
    return { ...indicator, ...getBgSx(identifier), ...getActiveSx(identifier) };
  };

  const getConnectorSx = (identifier: number) => {
    return { ...connector, ...getBgSx(identifier) };
  };

  const isValidDestination = (formIndex: number) => {
    const nameError = validateName(activity.activityName, true, t);
    if (nameError) {
      setNavigationError(nameError);
      setTimeout(() => setNavigationError(''), 2000);

      return false;
    }
    if (formIndex > 2 && activity.startDate.toISOString() == new Date(0).toISOString()) {
      setNavigationError(t('You need to select a date first') + '');
      setTimeout(() => setNavigationError(''), 2000);
      return false;
    }
    // Steg 4 (stjärnvärde) ligger efter längden — hoppa inte över ett tomt steg 3.
    if (formIndex > 3 && activity.duration === -1) {
      setNavigationError(t('You need to select a duration first') + '');
      setTimeout(() => setNavigationError(''), 2000);
      return false;
    }
    return true;
  };

  const navigateToStep = (form: ActivityForms) => {
    if (form === 'Title') {
      formHandler.goToForm(form);
    } else if (form === 'Date') {
      isValidDestination(2) && formHandler.goToForm(form);
    } else if (form === 'Duration') {
      isValidDestination(3) && formHandler.goToForm(form);
    } else if (form === 'StarValue') {
      isValidDestination(4) && formHandler.goToForm(form);
    }
  };

  const createAccessibleBox = (identifier: number, form: ActivityForms) => {
    const boxProps = {
      sx: getIndicatorSx(identifier),
      tabIndex: 0,
      role: 'button',
      'aria-label': t(`Go to ${form.toLowerCase()} step`),
      onKeyDown: (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigateToStep(form);
        }
      },
      onClick: () => navigateToStep(form),
    };

    return <Box {...boxProps}>{identifier}</Box>;
  };

  return (
    <>
      {formHandler.current.progressValue && (
        <Stack direction={'row'} alignItems="center">
          {createAccessibleBox(1, 'Title' as ActivityForms)}
          <Box sx={getConnectorSx(2)} />
          {createAccessibleBox(2, 'Date' as ActivityForms)}
          <Box sx={getConnectorSx(3)} />
          {createAccessibleBox(3, 'Duration' as ActivityForms)}
          {/* Stjärnvärdessteget finns bara i stjärn-configen (progressTotal 4). */}
          {totalSteps >= 4 && (
            <>
              <Box sx={getConnectorSx(4)} />
              {createAccessibleBox(4, 'StarValue' as ActivityForms)}
            </>
          )}
        </Stack>
      )}
      {navigationError.length ? (
        <Alert severity="error" sx={{ position: 'fixed', top: 0 }}>
          {navigationError}
        </Alert>
      ) : null}
    </>
  );
};

export default ProgressIndicator;
